import { useParams } from "react-router";
import { useLayoutEffect, useMemo } from "react";
import { readFragment } from "gql.tada";
import { Loading } from "./Loading";
import { NotFound } from "./NotFound";
import { MetadataHelmet } from "./MetadataHelmet";
import { useContent } from "@hooks/useContent";
import { useTheme } from "@hooks/useTheme";
import { GetCaseStudyPageBySlug } from "@queries/GetCaseStudyPageBySlug";
import { SlideCarouselSectionById } from "@queries/entries/SlideCarouselSectionById";
import { CaseStudyPageRenderer } from "./CaseStudyPageRenderer";
import { useScrollReset } from "@hooks/useScrollReset";
import { ThemeFragment } from "@queries/fragments/ThemeFragment";
import { defaultTheme } from "@utils/ThemeContext";
import { isValidTheme } from "../../types/ThemeModel";

export const CaseStudyPageResolver = () => {
  useScrollReset();
  const { slug } = useParams();

  const [response] = useContent({
    query: GetCaseStudyPageBySlug,
    variables: { slug: slug ?? "" },
  });

  const [page] = response.data?.caseStudyPageCollection?.items ?? [];

  // Case study pages are only served in locales their content has been
  // explicitly localized for — a null localeGate means the requested locale
  // would merely fall back to en-US content, so we 404 instead.
  const isAvailableForLocale = !!page?.localeGate;

  const carouselId = page?.adFormatsCarousel?.sys?.id ?? "";
  const [carouselResponse] = useContent({
    query: SlideCarouselSectionById,
    variables: { id: carouselId },
    pause: !carouselId || !isAvailableForLocale,
  });
  const carouselData = carouselResponse.data?.slideCarouselSection ?? null;

  // Theme handling mirrors Page.tsx
  const { setTheme } = useTheme();
  const theme = useMemo(
    () =>
      isAvailableForLocale
        ? readFragment(ThemeFragment, page?.theme ?? null) ?? defaultTheme
        : defaultTheme,
    [isAvailableForLocale, page?.theme]
  );

  useLayoutEffect(() => {
    if (!isValidTheme(theme)) {
      console.warn(`Palette combination invalid: `, theme);
      return;
    }
    setTheme(theme);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme]);

  if (response.fetching) return <Loading />;
  if (!slug || !page || !isAvailableForLocale) return <NotFound />;
  if (page.adFormatsCarousel && carouselResponse.fetching) return <Loading />;

  return (
    <>
      <MetadataHelmet pageInfo={{ slug: page.slug, metadata: page.metadata }} />
      <CaseStudyPageRenderer key={slug} data={page} carouselData={carouselData} />
    </>
  );
};
